import { FC } from 'react';
import { Progress } from '@new/common/ui/Progress/Progress';
import { useSingleCourse } from '@new/features/courses/contexts/SingleCourseProvider';
import { useGetCourseLessonsQuery } from '@teachur/http-client';
import { StepMessage } from '../StepMessage';

export const SelfAssessmentContent: FC = () => {
  const { course, activeStep } = useSingleCourse();

  const { data: lessons = [] } = useGetCourseLessonsQuery(course?.id ?? 0, {
    enabled: !!course && activeStep === 'self',
  });

  if (!course || activeStep !== 'self') return null;

  const progress =
    course.coursePassed || course.status === 'completed' || course.quizCompleted
      ? 100
      : course.selfAssessmentCompletionPercentage;
  const isFinished = progress === 100;
  const objectivesRemaining = lessons.reduce((total, lesson) => {
    const totalObjectives = lesson.totalObjectives ?? lesson.objectives?.length ?? 0;
    const completedObjectives =
      lesson.completedObjectives ?? lesson.completedObjectiveIds?.length ?? 0;

    return total + Math.max(totalObjectives - completedObjectives, 0);
  }, 0);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border-2 border-(--neutral-black-NB200) p-4 lg:p-8">
      <p className="[font-size:var(--fs-l4)]! lg:[font-size:var(--fs-l5)]! text-(--neutral-black-NB500)! m-0!">
        Your progress
      </p>
      <div className="flex items-center justify-between gap-4">
        <p
          className={`[font-size:var(--fs-l5)]! lg:[font-size:var(--fs-p3)]! leading-tight font-semibold ${
            isFinished ? 'text-(--success-S600)' : 'text-(--secondary-B900)'
          }! m-0!`}
        >
          {progress}% completed
        </p>

        <p className="[font-size:var(--fs-l5)]! lg:[font-size:var(--fs-p5)]! leading-tight text-right font-medium text-(--neutral-black-NB500)! m-0!">
          {objectivesRemaining} objectives are remaining to complete
        </p>
      </div>
      <Progress
        value={progress}
        color={isFinished ? 'var(--success-S600)' : 'var(--secondary-B900)'}
        className="h-3 lg:h-4.75 w-full"
      />
      {!isFinished && (
        <StepMessage
          variant="info"
          message="Start by reviewing the lesson objectives for this course. Mark each objective as completed as you work through them. When all objectives are completed, you will unlock the Course Exam step."
        />
      )}
    </div>
  );
};
